import axios from "axios";
import { getCookie } from "./cookie";
import { getAccessToken, setAccessToken } from "./tokenStore";
import { readLegacyToken } from "./legacyToken";

export type ClearLocalSessionOptions = {
  /**
   * Whether the session being ended is an admin impersonating a supplier.
   * Read it with `isImpersonatingSession()` BEFORE clearing any cookie: both
   * `removeItems()` in logoutAndRedirect and `clearAllCookies()` delete the
   * `isUserImpersonate` cookie it comes from.
   */
  impersonating: boolean;
};

/** True while the current cookies belong to an impersonated session. */
export const isImpersonatingSession = (): boolean =>
  Boolean(getCookie("isUserImpersonate"));

/**
 * True while any session cookie is left for an entry page (sign-in, verify)
 * to end. Those pages call clearLocalSession() only then: straight after
 * logoutAndRedirect or the _app.tsx expiry redirect the cookies, and the
 * impersonation flag with them, are already gone, so a second run would read
 * `impersonating: false` and undo an impersonated session's push skip (N-56).
 */
export const hasLocalSession = (): boolean =>
  Boolean(
    getAccessToken() ||
      readLegacyToken() ||
      getCookie("auth") ||
      getCookie("authenticated"),
  );

/**
 * This browser's push subscription for the supplier portal, if any. Never
 * throws: no service worker, no push support or a denied permission all
 * resolve to null (N-55).
 */
export const getPushSubscription =
  async (): Promise<PushSubscription | null> => {
    try {
      if (typeof navigator === "undefined") return null;
      const registration = await navigator.serviceWorker?.getRegistration();
      return (await registration?.pushManager?.getSubscription()) ?? null;
    } catch {
      return null;
    }
  };

/**
 * Client-side cleanup for every way a supplier session ends
 * (sellsmart-api#4334 S6.3, ADR-20): `logoutAndRedirect` (logout button, idle
 * timeout, refresh failure, 401s), the `_app.tsx` session-expiry redirect, and
 * the sign-in / verify pages that clear an existing session on entry.
 *
 * In this order:
 * 1. Deletes the `Authorization` default a successful token refresh sets on
 *    the global axios instance (utils/request.ts), so no later request can
 *    resend the dead token (N-53, N-65). This runs synchronously, before the
 *    returned promise settles, so callers that do not await still get it.
 * 2. Unless the session was an impersonation, removes this browser's push
 *    subscription and empties Cache Storage (S7, ADR-9), so a shared device
 *    stops receiving the supplier's notifications and drops cached pages.
 *    An admin impersonating a supplier must not remove that supplier's
 *    device (N-56). Failures are swallowed: logout must never be blocked.
 */
const clearLocalSession = async ({
  impersonating,
}: ClearLocalSessionOptions): Promise<void> => {
  delete axios.defaults.headers.common["Authorization"];
  // sellsmart-api#4626: the access token lives in memory only.
  setAccessToken(null);

  if (impersonating) return;

  try {
    const subscription = await getPushSubscription();
    await subscription?.unsubscribe();
  } catch {
    // Never block logout on push cleanup.
  }

  try {
    if (typeof window !== "undefined" && "caches" in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Never block logout on cache cleanup.
  }
};

/**
 * sellsmart-api#4626 AC-1.10: ends the session the HttpOnly refresh cookie
 * belongs to. A path that clears local state without calling the API (the
 * session-expiry redirect, the sign-in and verify pages on entry) would
 * otherwise leave the cookie live, and the next page load would sign the user
 * straight back in. A CORS-simple keepalive request (no Content-Type, no
 * body), so it needs no preflight and finishes while the page navigates
 * away; await it where a sign-in or a redemption follows. Never throws.
 */
export const revokeRefreshCookie = async (): Promise<void> => {
  try {
    await fetch(`${process.env.URL_API}/identity/suppliers/logout`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { Accept: "application/json" },
      // AbortSignal.timeout is missing before Safari 16 / Chrome 103; without
      // it the revoke still goes out, just without a timeout.
      signal:
        typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(10000) : undefined,
    });
  } catch (error) {
    // Nothing to do: the cookie expires with the browser session, or at its Max-Age.
  }
};

export default clearLocalSession;
